import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Filter, ListTodo, Plus, Search } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/domain/Bits";
import { TaskForm } from "../../components/domain/TaskForm";
import { TaskList, TaskShape } from "../../components/domain/TaskList";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

type StatusFilter = "active" | "open" | "in_progress" | "completed" | "cancelled" | "all";
type PriorityFilter = "all" | "low" | "normal" | "high" | "urgent";

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "active", label: "Active" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "all", label: "All statuses" },
];

const PRIORITY_OPTIONS: Array<{ value: PriorityFilter; label: string }> = [
  { value: "all", label: "All priorities" },
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];

function statusParam(status: StatusFilter): string | undefined {
  if (status === "active") return "open,in_progress";
  if (status === "all") return undefined;
  return status;
}

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<TaskShape[]>([]);
  const [status, setStatus] = useState<StatusFilter>("active");
  const [priority, setPriority] = useState<PriorityFilter>("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showTask, setShowTask] = useState(false);
  const canMutate = user?.tenant_role !== "viewer";

  const refetch = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/crm/tasks", {
        params: {
          status: statusParam(status),
          limit: 500,
          sort: status === "completed" || status === "cancelled" ? "updated" : "due",
        },
      });
      setTasks(response.data.items || []);
    } catch {
      setError("Could not load the Task Tracker.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { refetch(); }, [refetch]);

  const visibleTasks = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return tasks.filter((task) => {
      if (priority !== "all" && task.priority !== priority) return false;
      if (!needle) return true;
      return [task.title, task.notes, task.task_type_code]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(needle));
    });
  }, [tasks, priority, query]);

  const summary = useMemo(() => ({
    overdue: visibleTasks.filter((task) => task.overdue).length,
    dueToday: visibleTasks.filter((task) => task.due_today).length,
    completed: visibleTasks.filter((task) => task.status === "completed").length,
  }), [visibleTasks]);

  return (
    <>
      <PageHeader
        title="Task Tracker"
        subtitle="Plan, prioritize, and verify CRM work from one controlled queue"
      />
      <main className="mx-auto max-w-7xl space-y-5 p-6 md:p-8" data-testid="task-tracker">
        <section className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700">
              <AlertTriangle className="mr-1 inline h-4 w-4" /> {summary.overdue} overdue
            </span>
            <span className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              <ListTodo className="mr-1 inline h-4 w-4" /> {summary.dueToday} due today
            </span>
            <span className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700">
              <CheckCircle2 className="mr-1 inline h-4 w-4" /> {summary.completed} completed
            </span>
          </div>
          {canMutate && (
            <Button onClick={() => setShowTask(true)} data-testid="task-tracker-add-btn">
              <Plus className="mr-1 h-4 w-4" /> Add task
            </Button>
          )}
        </section>

        <section className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-[minmax(220px,1fr)_180px_180px]">
          <label className="relative">
            <span className="sr-only">Search tasks</span>
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search title, type, or notes"
              className="pl-9"
              data-testid="task-tracker-search"
            />
          </label>
          <label className="relative">
            <span className="sr-only">Filter by status</span>
            <Filter className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as StatusFilter)}
              className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm"
              data-testid="task-tracker-status"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr-only">Filter by priority</span>
            <select
              value={priority}
              onChange={(event) => setPriority(event.target.value as PriorityFilter)}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="task-tracker-priority"
            >
              {PRIORITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </section>

        <section className="rounded-lg border border-slate-200 bg-slate-50 p-4 md:p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Tasks</h2>
            <span className="text-xs text-slate-500" data-testid="task-tracker-count">
              {visibleTasks.length} shown
            </span>
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500" role="status">Loading tasks…</div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-red-600" role="alert">
              {error}
              <Button variant="outline" size="sm" onClick={refetch} className="ml-2">Retry</Button>
            </div>
          ) : (
            <TaskList
              tasks={visibleTasks}
              onRefetch={refetch}
              readOnly={!canMutate}
              emptyText="No tasks match these filters."
              testId="task-tracker-list"
            />
          )}
        </section>

        {!canMutate && (
          <p className="text-xs text-slate-500">Read-only access: task activity is visible, but changes are disabled.</p>
        )}
      </main>

      {canMutate && user && (
        <TaskForm
          open={showTask}
          onOpenChange={setShowTask}
          onSuccess={refetch}
          currentUserId={user.id}
        />
      )}
    </>
  );
}
